'use client';

import { SectorRotationResponse } from '@3fourteen/core';
import {
  ArrowLongDownIcon,
  ArrowLongUpIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/20/solid';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { classNames } from 'helpers/classNames';
import { memo, useState } from 'react';

import { TableSortDisplay } from 'components';

import { columns } from './columns';

interface SectorRotationTableProps extends SectorRotationResponse {}

function SectorRotationTable({ data }: SectorRotationTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'active', desc: true },
  ]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const sortedCol = sorting[0];
  const sortOption = sortedCol
    ? table.getColumn(sortedCol.id)?.columnDef.header as string
    : undefined;
  const sortOrder = sortedCol
    ? sortedCol.desc ? 'DESC' : 'ASC'
    : undefined;

  return (
    <div>
      <TableSortDisplay sortOption={sortOption ?? ''} sortOrder={sortOrder ?? 'ASC'} />
      <div className='table-container'>
        <table className='bg-white w-full border-collapse border-spacing-0'>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className='border-y border-solid border-zinc-300'>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className='py-1.5 px-3 first:pl-4 md:first:pl-6 sticky top-[-1px] uppercase text-sm font-bold text-left whitespace-nowrap bg-teal-100'
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}>
                    <span className='flex items-center'>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className='ml-1'>
                          {{
                            asc: <ArrowLongUpIcon className='h-4 w-4' />,
                            desc: <ArrowLongDownIcon className='h-4 w-4' />,
                          }[header.column.getIsSorted() as string] ?? (
                            <ArrowsUpDownIcon className='h-4 w-4 opacity-30' />
                          )}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const activeValue = row.getValue<number>('active');
              const activeClass =
                activeValue < 0
                  ? 'text-white bg-terracotta-500 font-bold'
                  : activeValue > 0
                  ? 'text-white bg-sage-500 font-bold'
                  : '';

              return (
                <tr
                  key={row.id}
                  className='border-y border-solid border-zinc-100'>
                  {row.getVisibleCells().map((cell) => {
                    const colId = cell.column.id;
                    const isWeightCell =
                      colId === 'benchmark' || colId === 'model' || colId === 'active';
                    const isActiveCell = colId === 'active';

                    return (
                      <td
                        key={cell.id}
                        className={classNames(
                          isActiveCell ? activeClass : undefined,
                          'first:pl-4 md:first:pl-6 border-r border-solid border-zinc-100 px-3 py-0 text-15 whitespace-nowrap'
                        )}>
                        {isWeightCell
                          ? `${cell.getValue()}%`
                          : flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default memo(SectorRotationTable);
